import React, { useState, useEffect } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { useQuery } from '@apollo/client'

import { CAN_MOVE_GUESTS_TO_GROUP } from '../../../graphql'
import { SmallLoader } from '../../../components/SimpleLoader/SimpleLoader'
import type { IFreeBookingGuest, IBookingGuest } from '../../../types'

interface GuestConfirmationModalProps {
  show: boolean
  onHide: () => void
  onContinue: () => void
  onGuestSelectionChange: (guestId: string, selected: boolean) => void
  externalGuests: (IFreeBookingGuest | IBookingGuest)[]
}

const GuestConfirmationModal: React.FC<GuestConfirmationModalProps> = ({
  show,
  onHide,
  onContinue,
  onGuestSelectionChange,
  externalGuests,
}) => {
  const [selectedGuestIds, setSelectedGuestIds] = useState<string[]>([])

  // Helper function to safely extract guest ID for both guest types
  const getGuestId = (guest: IFreeBookingGuest | IBookingGuest): string => {
    return guest.id
  }

  // Helper function to safely extract guest data for both guest types
  const getGuestData = (guest: IFreeBookingGuest | IBookingGuest) => {
    if ('addon' in guest) {
      // IFreeBookingGuest - has addon property and guest data is at guest level
      return guest.guest
    }
    // IBookingGuest - has option property and guest data is at guest level
    return guest.guest
  }

  // Extract guest IDs for the query
  const guestIds = externalGuests.map(getGuestId)

  const { data, loading, error } = useQuery(CAN_MOVE_GUESTS_TO_GROUP, {
    variables: { guests: guestIds },
    skip: !show || guestIds.length === 0,
    fetchPolicy: 'cache-and-network',
  })

  // Reset selected guests when modal opens/closes
  useEffect(() => {
    if (!show) {
      setSelectedGuestIds([])
    }
  }, [show])

  const handleCheckboxChange = (guestId: string, checked: boolean) => {
    if (checked) {
      setSelectedGuestIds(prev => [...prev, guestId])
    } else {
      setSelectedGuestIds(prev => prev.filter(id => id !== guestId))
    }

    // Update parent immediately
    onGuestSelectionChange(guestId, checked)
  }

  const handleContinue = () => {
    onContinue()
    setSelectedGuestIds([])
    onHide()
  }

  const handleClose = () => {
    setSelectedGuestIds([])
    onHide()
  }

  if (externalGuests.length === 0) {
    return null
  }

  // Filter guests based on API response
  const canMoveIds = data?.canMoveGuestsToGroup?.guests || []

  const movableGuests = externalGuests.filter(guest =>
    canMoveIds.includes(getGuestId(guest))
  )

  const nonMovableGuests = externalGuests.filter(
    guest => !canMoveIds.includes(getGuestId(guest))
  )

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Move Guests to Your Group</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        {loading && (
          <div className="text-center py-4">
            <SmallLoader />
            <p className="mt-2 mb-0">Checking guest availability...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-warning mb-3">
            <strong>Unable to verify guest availability.</strong>
            <br />
            You can continue, but guest selection will not be available.
          </div>
        )}

        {!loading && !error && (
          <>
            <p className="mb-3">
              The following guests are not part of your original group. Would
              you like to move any of them to your group for future bookings?
            </p>

            {movableGuests.length > 0 && (
              <div className="mb-5">
                <h6 className="text-success mb-2">Available to Move:</h6>
                {movableGuests.map(guest => {
                  const guestData = getGuestData(guest)
                  const guestId = getGuestId(guest)
                  const guestName = `${guestData.firstName} ${guestData.lastName}`

                  return (
                    <div key={guestId} className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`guest-${guestId}`}
                        checked={selectedGuestIds.includes(guestId)}
                        onChange={e =>
                          handleCheckboxChange(guestId, e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label ml-2"
                        htmlFor={`guest-${guestId}`}
                      >
                        {guestName}
                      </label>
                    </div>
                  )
                })}
              </div>
            )}

            {nonMovableGuests.length > 0 && (
              <div className="mb-3">
                <h6 className="text-muted mb-2">Cannot be Moved:</h6>
                <div className="alert alert-info py-1 mb-2">
                  <small>
                    The guests below cannot be moved because they are primary
                    contacts in their groups. Please contact the host if you
                    need to move them.
                  </small>
                </div>
                {nonMovableGuests.map(guest => {
                  const guestData = getGuestData(guest)
                  const guestId = getGuestId(guest)
                  const guestName = `${guestData.firstName} ${guestData.lastName}`

                  return (
                    <div key={guestId} className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`guest-readonly-${guestId}`}
                        checked={false}
                        disabled
                      />
                      <label
                        className="form-check-label ml-2 text-muted"
                        htmlFor={`guest-readonly-${guestId}`}
                      >
                        {guestName}
                      </label>
                    </div>
                  )
                })}
              </div>
            )}

            {!loading &&
              !error &&
              movableGuests.length === 0 &&
              nonMovableGuests.length === 0 && (
                <p className="text-muted text-center py-3">
                  No guests found to display.
                </p>
              )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleContinue}>
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default GuestConfirmationModal
